import { useMemo } from 'react';
import {
    createDate,
    createDateRange,
    format,
    formatDistanceStrict,
    formatTimeRemaining,
    isAfter,
    isBefore,
    subtract,
} from '@lingoda/dates';
import { useTimeRemainingAutoDisabled } from '@lingoda/hooks';
import { trans } from '@lingoda/i18n';
import { capitalize } from '@lingoda/utils';
import { ChalkboardTeacherIcon, ClockIcon, UserIcon } from '../../icons/lingodaIcons';
import { Avatar } from '../Avatar';
import { AvatarGroup } from '../AvatarGroup';
import { Grid } from '../GridNext';
import { Typography } from '../Typography';

interface Participant {
    id: number;
    name: string;
    photo?: string;
}

export interface MeetingCardDetailsProps {
    endDateIso: string;
    hasHost?: boolean;
    isActive?: boolean;
    participants?: Participant[];
    showTime?: boolean;
    showRemainingDays?: boolean;
    startDateIso: string;
    subtitle?: string;
}

export const MeetingCardDetails = ({
    endDateIso,
    hasHost,
    isActive,
    participants,
    showTime,
    showRemainingDays,
    startDateIso,
    subtitle,
}: MeetingCardDetailsProps) => {
    const startDate = createDate(startDateIso);
    const range = createDateRange(startDateIso, endDateIso, 'O');
    const subtitleColor = 'text.secondary';

    const now = useMemo(() => createDate(), []);
    const twentyFourHoursBeforeStart = subtract({ hours: 24 }, startDate);

    const isMoreThan24hBeforeStart = isBefore(now, twentyFourHoursBeforeStart);
    const isPastStart = isAfter(now, startDate);

    const useDaysFormatter = showRemainingDays && isMoreThan24hBeforeStart;
    const disableTimer =
        !isActive || (!showRemainingDays && isMoreThan24hBeforeStart) || isPastStart;

    const [timeRemaining, timeRemainingDisabled] = useTimeRemainingAutoDisabled(startDate, {
        formatter: useDaysFormatter ? daysFormatter : timeFormatter,
        getDisabled: () => disableTimer,
    });

    return (
        <>
            {subtitle && (
                <Grid display="flex" color={subtitleColor}>
                    <ChalkboardTeacherIcon size={16} />
                    <Typography variant="body3" ml={0.5} sx={{ overflowWrap: 'anywhere' }}>
                        {subtitle}
                    </Typography>
                </Grid>
            )}
            {showTime && (
                <Grid display="flex" color={subtitleColor}>
                    <ClockIcon size={16} />
                    <Typography variant="body3" ml={0.5} component="span" display="inline">
                        <Typography
                            variant="body3"
                            component="span"
                            display="inline"
                            sx={{ overflowWrap: 'anywhere' }}
                        >
                            {`${format('p', range.startDate)} - ${format('p', range.endDate)} ${
                                range.timezone
                            }`}
                        </Typography>
                        {!timeRemainingDisabled && (
                            <>
                                <Typography variant="body3" component="span" display="inline">
                                    {' • '}
                                </Typography>
                                <Typography
                                    variant="body3"
                                    sx={(theme) => ({ color: theme.colors.text.progress })}
                                    component="span"
                                    display="inline"
                                >
                                    {capitalize(timeRemaining)}
                                </Typography>
                            </>
                        )}
                    </Typography>
                </Grid>
            )}
            {!hasHost && (
                <Grid display="flex" color={subtitleColor}>
                    <UserIcon size={16} />
                    <Typography variant="body3" ml={0.5} sx={{ overflowWrap: 'anywhere' }}>
                        {trans('teacher-will-be-assigned', {}, 'student-common')}
                    </Typography>
                </Grid>
            )}
            <Grid display="flex" alignItems="center">
                <AvatarGroup max={6} counterSize={24}>
                    {participants?.map(({ id, name, photo }, index) => (
                        <Avatar
                            key={id}
                            size={24}
                            src={photo}
                            borderVariant="stacked"
                            sx={{ zIndex: index }}
                        >
                            {name}
                        </Avatar>
                    ))}
                </AvatarGroup>
            </Grid>
        </>
    );
};

const timeFormatter = (toDate: Date) => formatTimeRemaining(toDate, { addSuffix: true });
const daysFormatter = (toDate: Date) =>
    formatDistanceStrict(toDate, createDate(), {
        unit: 'day',
        addSuffix: true,
    });
